// Third-party library imports
import {
    get,
    includes,
    isEmpty,
    isEqual,
    map
} from 'lodash-es';
import {
    all,
    call,
    delay,
    fork,
    put,
    takeEvery,
    takeLatest,
    select
} from 'redux-saga/effects';

// Constants
import {
    API_NAME,
    IODM_COMPANY_STORAGE_NAME,
    IODM_PREVIEW_CUSTOMIZATION_STORAGE_NAME,
    maxAPIRefetchCount,
    refetchAPIDelay
} from '../../config/config';
import { defaultGuidValue } from '../../constants/common';

// GraphQL queries
import { _queries as queries } from '../../graphql/queries.graphql';

// Utility functions
import {
    checkShouldRequestRefetch,
    getRegionallyGraphqlResponse,
} from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';

// Redux types
import {
    Company,
    CompanyNotificationCustomization,
    CompanyUserRole
} from '../companies/types';

// Redux actions
import {
    confirmDetailsForFXConversionRequestAction,
    fetchNewNotificationsAction,
    fetchNotificationDetailsPublicAction,
    getCategorizedNotificationsData,
    getCategorizedNotificationsErrorAction,
    getCategorizedNotificationsRequestAction,
    getCategorizedNotificationsSuccessAction,
    getCorpayCurrenciesForCompanyRequestAction,
    getNotificationLetterPresignedUrlAction,
    getNotificationsByCategoryErrorAction,
    getNotificationsByCategoryRequestAction,
    getNotificationsByCategorySuccessAction,
    getPayingCountriesForTransferMateRequestAction,
    getPaymentDataPublicRequestAction,
    getProductAnnouncementsRequestAction,
    getTicketOptionsRequestAction,
    getUnreadNotificationsForUserRequestAction,
    getUnreadNotificationsForUserSuccessAction,
    makeTransfermatePaymentRequestAction,
    markNotificationAsReadAction,
    payUsingCreditsRequestAction,
    raiseQueryRequestAction,
} from './notificationSlice';
import { getOrganisationActionUrl } from '../organisations/organisationSlice';

// 8. Local imports � API
import client, { post } from 'src/api';

/**
 * Function for getting the 1st drawer data of notifications panel from API.
 * @param param0
 */
function* handleGetCategorizedNotificationsRequest({
    payload: sagaPayload,
}: any) {
    const { 
        companyId, 
        callback,
        noState
    } = sagaPayload;
    const errorMessage = 'Error fetching categorized notifications list. Please try again later.';

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CATEGORIZED_NOTIFICATIONS_FOR_USER,
                variables: {
                    CompanyId: companyId || null
                }
            }
        );

        const { Notifications } = get(
            res.data,
            'GetCategorizedNotificationsForUser'
        );

        const responsePayload = {
            data: Notifications
        };

        if (callback) {
            callback(Notifications);
        }

        if (!noState) {
            yield put(getUnreadNotificationsForUserRequestAction(companyId));
            yield put(
                getCategorizedNotificationsSuccessAction(responsePayload)
            );
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCategorizedNotificationsErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the 2nd drawer data of notifications panel from API.
 * @param param0
 */
function* handleGetNotificationsByCategoryRequest({
    payload
}: any) {
    const { companyId, categoryId } = payload;
    const errorMessage =
        'Error fetching notifications by category list. Please try again later.';

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_NOTIFICATIONS_BY_CATEGORY_FOR_USER,
                variables: {
                    CompanyId: companyId || null,
                    CategoryId: categoryId,
                }
            }
        );

        const { Notifications } = get(
            res.data,
            'GetNotificationsByCategoryForUser'
        );

        const responsePayload = {
            data: Notifications,
        };

        yield put(getNotificationsByCategorySuccessAction(responsePayload));
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        yield put(getNotificationsByCategoryErrorAction([errorMessage]));
    }
}

/**
 * Function for fetching the new notifications list from API.
 * @param param0
 */
function* handleFetchNewNotificationsRequest({ payload: sagaPayload }: any) {
    const { latestNotificationDateTime, callback } = sagaPayload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_NEW_NOTIFICATIONS_FOR_USER,
                variables: {
                    CreatedDateTime: latestNotificationDateTime || null,
                }
            }
        );

        const { Notifications } = get(res.data, 'GetNewNotificationsForUser');
        const responsePayload: DynamicObject = {
            data: Notifications,
            IsSuccess: true,
        };

        if (callback) {
            callback(responsePayload);
        }
    }
    catch (err) {
        if (callback) {
            callback({
                data: [],
                IsSuccess: false,
            });
        }

        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }
    }
}

let refetchCount = 0;
/**
 * Function for getting the data needed to populate the public Notification details page from API.
 * @param param0
 */
function* handleFetchNotificationDetailsPublic({ payload: sagaPayload }: any) {
    const {
        notificationId,
        applyDelay,
        isPaymentPlanEnabled,
        invoicesAndCreditsOnly,
        callback,
    } = sagaPayload;

    try {
        if (applyDelay) {
            // delay for 3 seconds
            yield delay(3000);
        }

        // preview notification page check
        let res: DynamicObject

        if (notificationId === defaultGuidValue) {
            if (invoicesAndCreditsOnly) {
                const jsonResponse: DynamicObject = yield call(
                    fetch,
                    `/notification-preview-invoices-credits-data.json`
                );

                res = yield jsonResponse.json();
            }
            else {
                const companyUserRole: CompanyUserRole = JSON.parse(localStorage.getItem(IODM_COMPANY_STORAGE_NAME) || '{}');
                const company: Company = get(companyUserRole, 'Company');
                const previewCustomization: CompanyNotificationCustomization = JSON.parse(localStorage.getItem(IODM_PREVIEW_CUSTOMIZATION_STORAGE_NAME) || '{}');

                Object.assign(company, {
                    NotificationCustomization: previewCustomization
                });

                res = {
                    "data": {
                        "GetNotificationCommunicationDetail": {
                            "Contents": [
                                { "TemplateS3Key": "EmailSubject.txt", "File": { "Id": defaultGuidValue } },
                                { "TemplateS3Key": "EmailBody.html", "File": { "Id": defaultGuidValue } },
                                { "TemplateS3Key": "SMS.html", "File": { "Id": defaultGuidValue } },
                                { "TemplateS3Key": "Letter.html", "File": { "Id": defaultGuidValue } },
                            ],
                            "Customer": {
                                "Id": defaultGuidValue,
                                "CustomerCode": "SAMPLE CUSTOMER CODE",
                                "DisplayName": "A Sample Customer"
                            },
                            "Company": company
                        }
                    }
                };
            }
        }
        else {
            res = yield call(
                [client, client.graphql],
                {
                    query: queries.GET_NOTIFICATION_COMMUNICATION_DETAIL,
                    variables: {
                        CommunicationId: notificationId || null,
                        AllInvoices: false,
                        PaymentPlanEnabled: isPaymentPlanEnabled,
                        InvoicesAndCreditsOnly: invoicesAndCreditsOnly,
                    }
                }
            );
        }

        res.IsSuccess = true;
        refetchCount = 0;

        if (callback) {
            callback(res);
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(
                fetchNotificationDetailsPublicAction(
                    notificationId,
                    applyDelay,
                    isPaymentPlanEnabled,
                    invoicesAndCreditsOnly,
                    callback
                )
            );
        }
        else if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

/**
 * Function for getting the list of unread notifications for the user logged in from API.
 */
function* handleGetUnreadNotificationsForUserRequest({ payload }: any) {
    const { companyId } = payload;
    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_UNREAD_NOTIFICATION_CATEGORIES_FOR_USER,
                variables: {
                    CompanyId: companyId || null
                }
            }
        );

        const unreadNotificationCategories = get(
            res.data,
            'GetUnreadNotificationCategoriesForUser'
        );

        yield put(
            getUnreadNotificationsForUserSuccessAction(
                unreadNotificationCategories
            )
        );
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }
    }
}

/**
 * Function for getting the presigned url for the letter found in Notification details page from API.
 * @param param0
 */
function* handleGetNotificationLetterPresignedUrl({
    payload: sagaPayload,
}: any) {
    const { fileId, callback } = sagaPayload;

    try {
        let apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_FILE_LOCATION,
            {
                FileId: fileId || null,
            }
        );

        res.IsSuccess = true;

        if (res.data.GetFileLocation) {
            res.data.GetFileLocation = get(res, 'data.GetFileLocation');
        }
        if (callback) callback(res);
    } catch(err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for getting the options for Step 1 in Inform Us panel from API.
 * @param param0
 */
function* handleGetTicketOptionsRequest({ payload: sagaPayload }: any) {
    const { companyId, callback } = sagaPayload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TICKET_OPTIONS_FOR_COMPANY,
                variables: {
                    CompanyId: companyId,
                }
            }
        );

        const { TicketOptions } = get(res, 'data.GetTicketOptionsForCompany');
        
        if (callback) {
            callback({
                IsSuccess: true,
                TicketOptions: TicketOptions || [],
                Messages: []
            });
        }
    }
    catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['Failed to fetch ticket options!'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.', err);
        }
    }
}

/**
 * Function called when submitting the query using Inform Us panel to the API.
 * @param param0
 */
function* handleRaiseQueryRequest({ payload: sagaPayload }: any) {
    const {
        InvoiceIds,
        CustomerId,
        CompanyId,
        TicketOptionId,
        WorkflowId,
        CommunicationId,
        Details,
        callback,
    } = sagaPayload;

    const payload: DynamicObject = {
        CompanyId,
        WorkflowId,
        TicketOption: {
            Id: TicketOptionId,
        },
        Invoices: map(InvoiceIds, (Id) => ({
            Id,
        })),
        Customer: {
            Id: CustomerId,
        },
        Details,
        Communication: {
            Id: CommunicationId,
        },
    };

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/ticket',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        const { TaskEditAction } = res;
        const successMessages: any = [];

        if (TaskEditAction) {
            successMessages.push(`Ticket has been sent successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };

            callback(response);
        }
    }
    catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for setting the notifications as read via an API.
 * @param param0
 */
function* handleMarkNotificationAsRead({ payload: sagaPayload }: any) {
    const { categoryId, notificationId, callback } = sagaPayload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.UPDATE_NOTIFICATION_IS_READ_FLAG,
                variables: {
                    CategoryId: categoryId,
                    NotificationId: notificationId,
                }
            }
        );

        const { UpdateNotificationIsReadFlag } = res.data;
        const successMessages: string[] = [];

        if (UpdateNotificationIsReadFlag) {
            successMessages.push(
                `Notification has been marked as read successfully!`
            );
        }

        console.log(successMessages, notificationId);

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };

            callback(response);
        }
    }
    catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called when connecting `Pay using credits` to the API.
 * @param param0
 */
function* handlePayUsingCreditsRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/credit/paynow',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        refetchCount = 0;

        if (callback) {
            if (isEmpty(get(res, 'Messages'))) {
                const response = {
                    ...res,
                    IsSuccess: true,
                };

                callback(response);
            }
            else {
                throw new Error('Failed');
            }
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(payUsingCreditsRequestAction(payload, callback));
        } else if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            }

            const firstErrorMessage = get(returnData, 'Messages.0', '');

            if (
                includes(firstErrorMessage, 'No credits to allocate') ||
                includes(
                    firstErrorMessage,
                    'No invoices to allocate credits to'
                )
            ) {
                returnData.IsSuccess = true;
            } else {
                returnData.IsSuccess = false;
            }
            callback(returnData);
        }
    }
}

/**
 * Function for getting the payment details of company publicly.
 * @param param0
 */
function* handleGetPaymentDataPublicRequest({ payload: sagaPayload }: any) {
    const { paymentId, companyId, callback } = sagaPayload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_PAYMENT_DETAILS_FOR_COMPANY_PUBLIC,
                variables: {
                    PaymentId: paymentId,
                    CompanyId: companyId,
                }
            }
        );

        const Payment = get(res.data, 'GetPublicPaymentDetails');

        if (callback) {
            callback(Payment);
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function that connects to fetching the products announcement list between the given dates.
 */
function* handleGetProductAnnouncementsRequest({ payload: sagaPayload }: any) {
    const { minDate, maxDate, callback } = sagaPayload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_PRODUCT_ANNOUNCEMENTS,
                variables: {
                    PublishDateTimeMin: minDate,
                    PublishDateTimeMax: maxDate,
                    Skip: 0,
                    PageSize: 1000,
                }
            }
        );

        const { ProductAnnouncements } = get(
            res.data,
            'GetProductAnnouncements'
        );

        refetchCount = 0;

        const responsePayload = {
            data: ProductAnnouncements,
            IsSuccess: true,
        };

        callback(responsePayload);
    }
    catch (err) {
        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(
                getProductAnnouncementsRequestAction(minDate, maxDate, callback)
            );
        } else {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

/**
 * Function called for getting the workflow step options for task editing.
 * @param param0
 */
 function* handleGetCorpayCurrenciesForCompanyRequest({ payload: sagaPayload }: any) {
    try {
        const { companyId, callback } = sagaPayload;
        
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CORPAY_CURRENCIES_FOR_COMPANY,
                variables: {
                    CompanyId: companyId,
                }
            }
        );

        const corpayCurrencies = get(res.data, 'GetCorpayCurrenciesForCompany');

        if (callback) {
            callback(corpayCurrencies);
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called for getting the workflow step options for task editing.
 * @param param0
 */
function* handleGetTransferMatePayingCountriesRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/transfermate/get-countries',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();

        refetchCount = 0;

        if (callback) {
            if (isEmpty(get(res, 'Messages'))) {
                const response = {
                    ...res.TransferMateCountries.Countries.CountryList,
                    IsSuccess: true,
                };
                callback(response);
            } else {
                throw new Error('Failed');
            }
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called when connecting `Pay using credits` to the API.
 * @param param0
 */
function* handleConfirmDetailsForFXConversionRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/transfermate/fx-conversion',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();

        refetchCount = 0;

        if (callback) {
            if (isEmpty(get(res, 'Messages'))) {
                const response = {
                    ...res,
                    IsSuccess: true,
                };
                callback(response);
            } else {
                throw new Error('Failed');
            }
        }
    } catch (err) {
        if (err instanceof Error) {
            const response = {
                    ErrorMessage: err, 
                    IsSuccess: false,
                };
                callback(response);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called when connecting `Pay using credits` to the API.
 * @param param0
 */
function* handleMakeTransferMatePaymentRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/transfermate/edu',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();

        refetchCount = 0;

        if (callback) {
            if (isEmpty(get(res, 'Messages'))) {
                if(res.TransferMatePaymentResponse.Operation.Result === 'failure'){
                    const response = {
                        ...res,
                        ErrorMessage: [res.TransferMatePaymentResponse.Operation.ErrorMessage],
                        IsSuccess: false,
                    };
                    callback(response);
                } else {
                    const response = {
                        ...res,
                        IsSuccess: true,
                    };
                    callback(response);
                }
                
            } else {
                throw new Error('Failed');
            }
        }
    } catch (err) {
       let messages: string[] = [];

        const apiMessages = get(err, 'response.data.Messages');
        if (Array.isArray(apiMessages)) {
            messages = apiMessages;
        } else if (apiMessages) {
            messages = [apiMessages];
        } 
        else if (err instanceof Error && err.message) {
            messages = [err.message];
        } else {
            messages = ['An unknown error occurred.'];
        }

        const response = {
            ErrorMessage: messages,
            IsSuccess: false,
        };
        callback(response);
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetCategorizedNotificationsRequest() {
    yield takeLatest(
        getCategorizedNotificationsRequestAction.type,
        handleGetCategorizedNotificationsRequest
    );
}

function* watchGetNotificationsByCategoryRequest() {
    yield takeLatest(
        getNotificationsByCategoryRequestAction.type,
        handleGetNotificationsByCategoryRequest
    );
}

function* watchFetchNewNotificationsRequest() {
    yield takeLatest(
        fetchNewNotificationsAction.type,
        handleFetchNewNotificationsRequest
    );
}

function* watchFetchNotificationDetailsPublic() {
    yield takeEvery(
        fetchNotificationDetailsPublicAction.type,
        handleFetchNotificationDetailsPublic
    );
}

function* watchGetUnreadNotificationsForUserRequest() {
    yield takeLatest(
        getUnreadNotificationsForUserRequestAction.type,
        handleGetUnreadNotificationsForUserRequest
    );
}

function* watchGetNotificationLetterPresignedUrl() {
    yield takeLatest(
        getNotificationLetterPresignedUrlAction.type,
        handleGetNotificationLetterPresignedUrl
    );
}

function* watchGetTicketOptionsRequest() {
    yield takeLatest(
        getTicketOptionsRequestAction.type,
        handleGetTicketOptionsRequest
    );
}

function* watchRaiseQueryRequest() {
    yield takeLatest(
        raiseQueryRequestAction.type,
        handleRaiseQueryRequest
    );
}

function* watchMarkNotificationAsRead() {
    yield takeLatest(
        markNotificationAsReadAction.type,
        handleMarkNotificationAsRead
    );
}

function* watchPayUsingCreditsRequest() {
    yield takeLatest(
        payUsingCreditsRequestAction.type,
        handlePayUsingCreditsRequest
    );
}

function* watchGetPaymentDataPublicRequest() {
    yield takeLatest(
        getPaymentDataPublicRequestAction.type,
        handleGetPaymentDataPublicRequest
    );
}

function* watchGetProductAnnouncementsRequest() {
    yield takeLatest(
        getProductAnnouncementsRequestAction.type,
        handleGetProductAnnouncementsRequest
    );
}

function* watchGetCorpayCurrenciesForCompanyRequest() {
    yield takeLatest(
        getCorpayCurrenciesForCompanyRequestAction.type,
        handleGetCorpayCurrenciesForCompanyRequest
    );
}

function* watchGetTransferMatePayingCountriesRequest() {
    yield takeLatest(
        getPayingCountriesForTransferMateRequestAction.type,
        handleGetTransferMatePayingCountriesRequest
    );
}

function* watchConfirmDetailsForFXConversionRequest() {
    yield takeLatest(
        confirmDetailsForFXConversionRequestAction.type,
        handleConfirmDetailsForFXConversionRequest
    );
}

function* watchMakeTransferMatePaymentRequest() {
    yield takeLatest(
        makeTransfermatePaymentRequestAction.type,
        handleMakeTransferMatePaymentRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* notificationsSaga() {
    yield all([
        fork(watchGetCategorizedNotificationsRequest),
        fork(watchGetNotificationsByCategoryRequest),
        fork(watchFetchNewNotificationsRequest),
        fork(watchFetchNotificationDetailsPublic),
        fork(watchGetUnreadNotificationsForUserRequest),
        fork(watchGetNotificationLetterPresignedUrl),
        fork(watchGetTicketOptionsRequest),
        fork(watchRaiseQueryRequest),
        fork(watchMarkNotificationAsRead),
        fork(watchPayUsingCreditsRequest),
        fork(watchGetPaymentDataPublicRequest),
        fork(watchGetProductAnnouncementsRequest),
        fork(watchGetCorpayCurrenciesForCompanyRequest),
        fork(watchGetTransferMatePayingCountriesRequest),
        fork(watchConfirmDetailsForFXConversionRequest),
        fork(watchMakeTransferMatePaymentRequest)
    ]);
}

export default notificationsSaga;
